/**
 * Phase 4: ETag storage and retrieval for HTTP caching
 * Stores ETags per URL in localStorage to enable 304 Not Modified responses
 *
 * Implements LRU cache eviction to prevent quota exceeded errors
 */

const ETAG_STORAGE_PREFIX = "etag:";
const DATA_STORAGE_PREFIX = "cache:";
const TIMESTAMP_STORAGE_PREFIX = "ts:";

// Maximum cache size: 4MB (leaves ~1MB headroom from typical 5MB quota)
const MAX_CACHE_SIZE_BYTES = 4 * 1024 * 1024;
// When cache reaches this threshold, start evicting old entries
const EVICTION_THRESHOLD_BYTES = 3.5 * 1024 * 1024;

/**
 * Estimates the size of a string in bytes (UTF-8 encoding)
 */
function estimateSize(str: string): number {
    return new Blob([str]).size;
}

/**
 * Gets all cache entries with their sizes and timestamps
 */
function getCacheEntries(): Array<{ key: string; size: number; timestamp: number; url: string }> {
    const entries: Array<{ key: string; size: number; timestamp: number; url: string }> = [];

    try {
        const keys = Object.keys(localStorage);
        for (const key of keys) {
            if (key.startsWith(DATA_STORAGE_PREFIX)) {
                const url = key.substring(DATA_STORAGE_PREFIX.length);
                const data = localStorage.getItem(key);
                const timestampKey = `${TIMESTAMP_STORAGE_PREFIX}${url}`;
                const timestampStr = localStorage.getItem(timestampKey);
                const timestamp = timestampStr ? parseInt(timestampStr, 10) : 0;

                if (data) {
                    entries.push({
                        key,
                        size: estimateSize(key) + estimateSize(data),
                        timestamp,
                        url
                    });
                }
            }
        }
    } catch (error) {
        console.warn("[ETag] Failed to get cache entries:", error);
    }

    return entries;
}

/**
 * Estimates total cache size
 */
function estimateCacheSize(): number {
    let totalSize = 0;

    try {
        const keys = Object.keys(localStorage);
        for (const key of keys) {
            if (key.startsWith(ETAG_STORAGE_PREFIX) ||
                key.startsWith(DATA_STORAGE_PREFIX) ||
                key.startsWith(TIMESTAMP_STORAGE_PREFIX)) {
                const value = localStorage.getItem(key);
                if (value) {
                    totalSize += estimateSize(key) + estimateSize(value);
                }
            }
        }
    } catch (error) {
        console.warn("[ETag] Failed to estimate cache size:", error);
    }

    return totalSize;
}

/**
 * Evicts oldest cache entries until cache is below threshold
 */
function evictOldEntries(targetSize: number = EVICTION_THRESHOLD_BYTES): void {
    try {
        const entries = getCacheEntries();

        // Sort by timestamp (oldest first)
        entries.sort((a, b) => a.timestamp - b.timestamp);

        let evictedCount = 0;

        // Remove oldest entries until we're below threshold
        for (const entry of entries) {
            const currentSize = estimateCacheSize();
            if (currentSize <= targetSize) {
                break;
            }

            // Calculate full size of this entry (data + etag + timestamp)
            const etagKey = `${ETAG_STORAGE_PREFIX}${entry.url}`;
            const timestampKey = `${TIMESTAMP_STORAGE_PREFIX}${entry.url}`;
            const etag = localStorage.getItem(etagKey);
            const timestamp = localStorage.getItem(timestampKey);

            let entrySize = entry.size; // Data size
            if (etag) {
                entrySize += estimateSize(etagKey) + estimateSize(etag);
            }
            if (timestamp) {
                entrySize += estimateSize(timestampKey) + estimateSize(timestamp);
            }

            // Remove all cache entries for this URL
            clearETagCacheForUrl(entry.url);
            evictedCount++;
        }

        if (evictedCount > 0) {
            console.log(`[ETag] Evicted ${evictedCount} cache entries to free space`);
        }
    } catch (error) {
        console.warn("[ETag] Failed to evict old entries:", error);
    }
}

/**
 * Updates the timestamp for a URL (marks it as recently used)
 */
function updateTimestamp(url: string): void {
    try {
        const timestampKey = `${TIMESTAMP_STORAGE_PREFIX}${url}`;
        localStorage.setItem(timestampKey, Date.now().toString());
    } catch (error) {
        // Ignore timestamp update errors - not critical
    }
}

/**
 * Gets the stored ETag for a URL
 */
export function getStoredETag(url: string): string | null {
    try {
        const key = `${ETAG_STORAGE_PREFIX}${url}`;
        const etag = localStorage.getItem(key);
        if (etag) {
            // Update timestamp when accessed (LRU)
            updateTimestamp(url);
        }
        return etag;
    } catch (error) {
        // localStorage might not be available (e.g., incognito mode)
        console.warn("[ETag] Failed to get stored ETag:", error);
        return null;
    }
}

/**
 * Stores an ETag for a URL
 */
export function storeETag(url: string, etag: string): void {
    try {
        // Check cache size and evict if needed
        const currentSize = estimateCacheSize();
        if (currentSize >= EVICTION_THRESHOLD_BYTES) {
            evictOldEntries();
        }

        const key = `${ETAG_STORAGE_PREFIX}${url}`;
        localStorage.setItem(key, etag);
        updateTimestamp(url);
    } catch (error) {
        // If quota exceeded, try to evict old entries and retry once
        if (error instanceof DOMException && error.name === "QuotaExceededError") {
            console.warn("[ETag] Quota exceeded, attempting to free space...");
            evictOldEntries(MAX_CACHE_SIZE_BYTES * 0.5); // Evict down to 50% of max
            try {
                const key = `${ETAG_STORAGE_PREFIX}${url}`;
                localStorage.setItem(key, etag);
                updateTimestamp(url);
            } catch (retryError) {
                console.warn("[ETag] Failed to store ETag after eviction:", retryError);
            }
        } else {
            console.warn("[ETag] Failed to store ETag:", error);
        }
    }
}

/**
 * Gets cached data for a URL
 */
export function getCachedData<T>(url: string): T | null {
    try {
        const key = `${DATA_STORAGE_PREFIX}${url}`;
        const cached = localStorage.getItem(key);
        if (cached) {
            // Update timestamp when accessed (LRU)
            updateTimestamp(url);
            return JSON.parse(cached) as T;
        }
    } catch (error) {
        // localStorage might not be available or data is corrupted
        console.warn("[ETag] Failed to get cached data:", error);
    }
    return null;
}

/**
 * Stores data for a URL (used when we get a 304 and need to return cached data)
 */
export function storeCachedData<T>(url: string, data: T): void {
    try {
        // Check cache size and evict if needed before storing
        const dataStr = JSON.stringify(data);
        const dataSize = estimateSize(dataStr);
        const currentSize = estimateCacheSize();

        // If this single item would exceed max size, don't cache it
        if (dataSize > MAX_CACHE_SIZE_BYTES * 0.5) {
            console.warn(`[ETag] Response too large to cache (${Math.round(dataSize / 1024)}KB), skipping cache`);
            return;
        }

        if (currentSize + dataSize >= EVICTION_THRESHOLD_BYTES) {
            evictOldEntries();
        }

        const key = `${DATA_STORAGE_PREFIX}${url}`;
        localStorage.setItem(key, dataStr);
        updateTimestamp(url);
    } catch (error) {
        // If quota exceeded, try to evict old entries and retry once
        if (error instanceof DOMException && error.name === "QuotaExceededError") {
            console.warn("[ETag] Quota exceeded, attempting to free space...");
            evictOldEntries(MAX_CACHE_SIZE_BYTES * 0.5); // Evict down to 50% of max
            try {
                const dataStr = JSON.stringify(data);
                const key = `${DATA_STORAGE_PREFIX}${url}`;
                localStorage.setItem(key, dataStr);
                updateTimestamp(url);
            } catch (retryError) {
                console.warn("[ETag] Failed to store cached data after eviction:", retryError);
            }
        } else {
            console.warn("[ETag] Failed to store cached data:", error);
        }
    }
}

/**
 * Clears all cached ETags and data
 */
export function clearETagCache(): void {
    try {
        const keys = Object.keys(localStorage);
        keys.forEach((key) => {
            if (key.startsWith(ETAG_STORAGE_PREFIX) ||
                key.startsWith(DATA_STORAGE_PREFIX) ||
                key.startsWith(TIMESTAMP_STORAGE_PREFIX)) {
                localStorage.removeItem(key);
            }
        });
    } catch (error) {
        console.warn("[ETag] Failed to clear cache:", error);
    }
}

/**
 * Clears cached data for a specific URL
 */
export function clearETagCacheForUrl(url: string): void {
    try {
        localStorage.removeItem(`${ETAG_STORAGE_PREFIX}${url}`);
        localStorage.removeItem(`${DATA_STORAGE_PREFIX}${url}`);
        localStorage.removeItem(`${TIMESTAMP_STORAGE_PREFIX}${url}`);
    } catch (error) {
        console.warn("[ETag] Failed to clear cache for URL:", error);
    }
}

/**
 * Gets cache statistics (for debugging)
 */
export function getCacheStats(): { sizeBytes: number; entryCount: number; maxSizeBytes: number } {
    const entries = getCacheEntries();
    return {
        sizeBytes: estimateCacheSize(),
        entryCount: entries.length,
        maxSizeBytes: MAX_CACHE_SIZE_BYTES
    };
}

/**
 * Builds a cache key from a URL and params
 */
export function buildCacheKey(baseUrl: string, params?: Record<string, unknown>): string {
    if (!params || Object.keys(params).length === 0) {
        return baseUrl;
    }

    // Sort params for consistent cache keys
    const sortedParams = Object.keys(params)
        .sort()
        .filter((key) => params[key] !== undefined && params[key] !== null)
        .map((key) => `${key}=${encodeURIComponent(String(params[key]))}`)
        .join("&");

    return `${baseUrl}?${sortedParams}`;
}